// Stream (Twitch) character format — serialize a unit to the fixed-position text
// block the FFTBG stream uses. Confirmed against real exports incl. a Mime: every
// unit is a rigid line frame (each slot a line, BLANK when empty — never omitted):
//
//   Name
//   Gender            ("Monster" for monsters)
//   Sign
//   Brave
//   Faith
//   Class             (monster name for monsters)
//   Secondary skillset
//   Reaction
//   Support
//   Move
//   <blank>
//   Mainhand
//   Offhand
//   Head
//   Armor
//   Accessory
//   <blank>
//   primary-class abilities, comma-separated
//   secondary-skillset abilities, comma-separated
//
// Monsters keep the frame with Gender="Monster", Class=monster name, and the
// skill/gear/ability lines blank. A single-character export optionally leads with
// "Player: !<name>" (the Team/Palettes header is deferred — teams come later).

const LF = '\n';

export function exportUnit(unit, playerName) {
  const u = unit || {};
  const isMon = (u.Gender || '') === 'Monster';
  const out = [];
  if (playerName) out.push('Player: !' + playerName, '');
  out.push(
    u.Name || '',
    isMon ? 'Monster' : (u.Gender || ''),
    u.Sign || '',
    u.Brave == null ? '' : String(u.Brave),
    u.Faith == null ? '' : String(u.Faith),
    u.Class || '',
    u.ActionSkill || '',          // secondary skillset name
    u.ReactionSkill || '',
    u.SupportSkill || '',
    u.MoveSkill || '',
    '',                            // separator
    u.Mainhand || '',
    u.Offhand || '',
    u.Head || '',
    u.Armor || '',
    u.Accessory || '',
    '',                            // separator
    (u.ClassSkills || []).join(', '),   // the unit's own class skillset abilities
    (u.ExtraSkills || []).join(', '),   // the secondary skillset's abilities
  );
  return out.join(LF);
}

// Parse a pasted stream block back into a unit (the FIRST unit if a whole team is
// pasted). Tolerant of an optional Player/Team/Palettes header and leading/trailing
// blank lines; the unit body itself is read positionally (the frame above), so a
// verbatim paste — every line present — is what it expects.
export function parseUnit(text) {
  const lines = String(text == null ? '' : text).replace(/\r/g, '').split('\n');
  let player = '';
  // strip a leading header (Player/Team/Palettes) + blank lines
  while (lines.length) {
    const l = lines[0].trim();
    if (/^player\s*:/i.test(l)) { player = l.replace(/^player\s*:\s*!?/i, '').trim(); lines.shift(); continue; }
    if (/^(team|palettes)\s*:/i.test(l)) { lines.shift(); continue; }
    if (l === '') { lines.shift(); continue; }
    break;
  }
  const L = (i) => (lines[i] == null ? '' : lines[i].trim());
  const csv = (s) => (s ? s.split(',').map((x) => x.trim()).filter(Boolean) : []);
  const num = (s) => { const n = parseInt(s, 10); return Number.isFinite(n) ? n : null; };
  return {
    player,
    Name: L(0),
    Gender: L(1),                  // 'Male' | 'Female' | 'Monster'
    Sign: L(2),
    Brave: num(L(3)),
    Faith: num(L(4)),
    Class: L(5),                   // class, or monster name when Gender === 'Monster'
    ActionSkill: L(6),             // secondary skillset
    ReactionSkill: L(7),
    SupportSkill: L(8),
    MoveSkill: L(9),
    // L(10) separator
    Mainhand: L(11),
    Offhand: L(12),
    Head: L(13),
    Armor: L(14),
    Accessory: L(15),
    // L(16) separator
    ClassSkills: csv(L(17)),
    ExtraSkills: csv(L(18)),
  };
}

// --- shareable-link codec: base64url of the export text -----------------------
// Reuses the export/parse round-trip so a ?build&u=<...> link is the same data as
// a copy-pasted block. URL-safe base64 (no +, /, =), UTF-8 safe.
function b64urlEncode(str) {
  const bin = unescape(encodeURIComponent(str));
  const b64 = (typeof btoa === 'function') ? btoa(bin) : Buffer.from(bin, 'binary').toString('base64');
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
function b64urlDecode(s) {
  s = String(s).replace(/-/g, '+').replace(/_/g, '/');
  const pad = s.length % 4 ? '='.repeat(4 - (s.length % 4)) : '';
  const bin = (typeof atob === 'function') ? atob(s + pad) : Buffer.from(s + pad, 'base64').toString('binary');
  return decodeURIComponent(escape(bin));
}

export function encodeBuild(unit, playerName) { return b64urlEncode(exportUnit(unit, playerName)); }
export function decodeBuild(param) { return parseUnit(b64urlDecode(param)); }
